import React, { useRef, useState, useMemo } from 'react'
import { Recipe, JobStatus } from '@pollination-solutions/pollination-sdk'
import RecipeGraph from './graph/RecipeGraph'
import { useDetailContext, usePanControls } from '~/hooks'
import { context as DetailContext } from '~/state/detailContext'
import DetailCard from './meta/DetailCard'
import { Graph } from '~/types'

interface GraphViewerProps {
  mode: 'recipe' | 'simulation'
  recipe: Recipe
  simulation?: JobStatus
}

const GraphViewer: React.FunctionComponent<GraphViewerProps> = ({ mode, recipe, simulation }): JSX.Element => {
  const graphRef = useRef<HTMLDivElement>(null)
  const [direction, setDirection] = useState<'LR' | 'TB'>('LR')

  const context = useDetailContext(recipe)

  const {
    transform: [tx, ty],
    clearTransform,
  } = usePanControls(graphRef, context.reference !== undefined)

  const config: Graph.Config = useMemo(() => {
    return { dir: direction }
  }, [direction])

  return (
    <DetailContext.Provider value={context}>
      <div className="graph-container" ref={graphRef}>
        <div className="graph-controls">
          <button onClick={clearTransform} className="control-reset" title="Reset Graph Position">
            R
          </button>
          <button
            onClick={() => (direction === 'LR' ? setDirection('TB') : setDirection('LR'))}
            className="control-reset"
          >
            D
          </button>
        </div>
        <div className="graph-image">
          {mode === 'recipe' ? (
            <RecipeGraph recipe={recipe} position={[tx, ty]} config={config} />
          ) : (
            <div>{simulation}</div>
          )}
        </div>
        <DetailCard />
      </div>
      <style jsx>{`
        .graph-container {
          width: 100%;
          height: 100%;
          background: none;
          position: relative;
          overflow: hidden;
        }

        .graph-controls {
          position: absolute;
          left: 30;
          top: 30;
          padding: 9px;
          z-index: 10;
        }

        .graph-controls > button {
          margin-right: 10px;
          height: 26px;
          width: 26px;
          border-radius: 3px;
          border: 2px solid #555;
          background: #fff;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .graph-controls > button:hover {
          background: #dcdcdc;
          cursor: pointer;
        }

        .graph-image {
          width: 100%;
          height: 100%;
          z-index: 5;
          position: absolute;
          left: 0;
          top: 0;
        }
      `}</style>
    </DetailContext.Provider>
  )
}

export default GraphViewer
